import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';

type S = components['schemas'];
export type Attempt = S['Attempt'];
export type AttemptCase = S['AttemptCase'];
export type AttemptQuestion = S['AttemptQuestion'];
export type AttemptResults = S['AttemptResults'];
export type SimulatorConfig = S['SimulatorConfig'];
export type HistoryItem = S['SimulatorHistoryItem'];
export type ExamCatalog = S['ExamCatalog'];
export type ExamItem = ExamCatalog['items'][number];
export type ProgressSummary = S['ProgressSummary'];
export type QuestionIssue = S['QuestionIssue'];
export type QuestionReport = S['QuestionReport'];
export type Letra = 'A' | 'B' | 'C' | 'D';
export type Tipo = 'simulator' | 'exam';

/** Rutas de la SPA y de la API según el tipo de intento. */
export const RUTAS = {
  simulator: { app: '/simulador', api: '/api/v1/simulator/attempts' },
  exam: { app: '/curso-enarm/examenes-prime/intento', api: '/api/v1/exam-attempts' },
} as const;

export const useSimuladorConfig = () =>
  useQuery({
    queryKey: ['simulador', 'config'],
    queryFn: () => unwrap<SimulatorConfig>(api.GET('/api/v1/simulator/config')),
  });

export const useHistorialSimulador = () =>
  useQuery({
    queryKey: ['simulador', 'historial'],
    queryFn: () => unwrap<HistoryItem[]>(api.GET('/api/v1/simulator/attempts')),
  });

export function useCrearSimulador() {
  return useMutation({
    mutationFn: (body: {
      mode: 'enarm' | 'entrenamiento';
      category: 'enarm' | 'aleatorio' | 'por-especialidad';
      cases: number;
      specialties?: number[];
    }) => unwrap<{ id: number; demo: boolean }>(api.POST('/api/v1/simulator/attempts', { body })),
  });
}

export function useImportarSimulador() {
  return useMutation({
    mutationFn: (name: string) =>
      unwrap<{ id: number }>(api.POST('/api/v1/simulator/attempts/import', { body: { name } })),
  });
}

export const useExamenes = (type: 'exams' | 'drills') =>
  useQuery({
    queryKey: ['examenes', type],
    queryFn: () => unwrap<ExamCatalog>(api.GET('/api/v1/exams', { params: { query: { type } } })),
  });

export function useIniciarExamen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; restart?: boolean }) =>
      unwrap<{ id: number; status: Attempt['status'] }>(
        api.POST('/api/v1/exams/{id}/attempts', {
          params: { path: { id: String(v.id) } },
          body: { restart: v.restart ?? false },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['examenes'] }),
  });
}

async function attemptGet<T>(tipo: Tipo, id: number, sub = ''): Promise<T> {
  const res = await api.GET(`${RUTAS[tipo].api}/{id}${sub}` as '/api/v1/simulator/attempts/{id}', {
    params: { path: { id: String(id) } },
  });
  return unwrap<T>(res as never);
}

async function attemptSend<T>(
  method: 'PUT' | 'POST',
  tipo: Tipo,
  id: number,
  sub: string,
  body?: unknown,
) {
  const fn = method === 'PUT' ? api.PUT : api.POST;
  const res = await fn(`${RUTAS[tipo].api}/${String(id)}${sub}` as never, { body } as never);
  return unwrap<T>(res as never);
}

export const useIntento = (tipo: Tipo, id: number) =>
  useQuery({
    queryKey: ['intento', tipo, id],
    queryFn: () => attemptGet<Attempt>(tipo, id),
    retry: false,
    refetchOnWindowFocus: false,
    // Las URLs firmadas de las imágenes viven 60 s: se renuevan sin tocar las respuestas locales.
    refetchInterval: 50_000,
  });

export const useResultados = (tipo: Tipo, id: number) =>
  useQuery({
    queryKey: ['resultados', tipo, id],
    queryFn: () => attemptGet<AttemptResults>(tipo, id, '/results'),
    retry: false,
  });

export interface Guardado {
  answers: { caseIndex: number; questionId: number; option: Letra | null }[];
  times: { caseIndex: number; seconds: number }[];
}

export const guardarRespuestas = (tipo: Tipo, id: number, body: Guardado) =>
  attemptSend<{ revision: number; answeredCount: number; status: Attempt['status'] }>(
    'PUT',
    tipo,
    id,
    '/answers',
    body,
  );

export const confirmarCaso = (tipo: Tipo, id: number, index: number) =>
  attemptSend<AttemptCase>('POST', tipo, id, `/cases/${String(index)}/confirm`);

export const cambiarEstado = (tipo: Tipo, id: number, accion: 'pause' | 'finish') =>
  attemptSend<{ status: Attempt['status'] }>('POST', tipo, id, `/${accion}`);

export const useAvances = (habilitado = true) =>
  useQuery({
    queryKey: ['avances'],
    queryFn: () => unwrap<ProgressSummary>(api.GET('/api/v1/progress/summary')),
    enabled: habilitado,
  });

export const useMotivosReporte = () =>
  useQuery({
    queryKey: ['catalogos', 'motivos-reporte'],
    queryFn: () => unwrap<QuestionIssue[]>(api.GET('/api/v1/catalogs/question-issues')),
    staleTime: 60 * 60_000,
  });

export const useMisReportes = (questionId: number | null) =>
  useQuery({
    queryKey: ['reportes', questionId],
    queryFn: () =>
      unwrap<QuestionReport[]>(
        api.GET('/api/v1/questions/{id}/reports', {
          params: { path: { id: String(questionId) } },
        }),
      ),
    enabled: questionId !== null,
  });

export function useReportar(questionId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { issueKey: number; comment: string }) =>
      unwrap(
        api.POST('/api/v1/questions/{id}/reports', {
          params: { path: { id: String(questionId) } },
          body: b,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reportes', questionId] }),
  });
}
